<template>
  <div class="cart-freebie-card" :class="{ [$class('loading')]: !product }">
    <!-- Multi-variant card mode: one card with option dropdowns -->
    <template v-if="isMultiVariantMode">
      <component
        :is="showCheckbox ? 'label' : 'div'"
        class="cart-freebie-card__card cart-freebie-card__card--multi-variant"
        :for="showCheckbox ? name : undefined"
      >
        <template v-if="showCheckbox">
          <input
            :id="name"
            :checked="selected"
            class="cart-freebie-card__input"
            :disabled="multiVariantCheckboxDisabled"
            :name="name"
            type="checkbox"
            @input.prevent.stop="handleMultiVariantCheckboxChange"
          />
          <span
            class="cart-freebie-card__checkmark cart-freebie-card__checkbox"
            aria-hidden="true"
          >
            <icon-check-outline class="cart-freebie-card__check" />
          </span>
        </template>

        <div class="cart-freebie-card__image-container">
          <responsive-image
            class="cart-freebie-card__image"
            :image="multiVariantImage"
            ratio="1:1"
            sizes="44px"
            widths="44, 106, 159"
          />
        </div>

        <div class="cart-freebie-card__options">
          <p
            class="cart-freebie-card__title text-mobile-paragraph-m-bold text-desktop-paragraph-m-bold"
            v-text="product.title"
          />
          <div
            v-for="(option, optionIndex) of filteredOptions"
            :key="option.name"
            class="cart-freebie-card__option-group"
          >
            <p
              class="cart-freebie-card__option-label text-mobile-paragraph-xs-bold text-desktop-paragraph-xs-bold"
              v-text="option.name"
            />
            <div
              v-if="
                optionIndex > 0 &&
                getOptionValues(option.name, optionIndex).length < 3
              "
              class="cart-freebie-card__option-pills"
            >
              <button
                v-for="value of getOptionValues(option.name, optionIndex)"
                :key="value"
                type="button"
                class="cart-freebie-card__option-pill text-mobile-paragraph-xs-regular text-desktop-paragraph-xs-regular"
                :class="{
                  'cart-freebie-card__option-pill--selected':
                    selectedOptions[option.name] === value,
                  'cart-freebie-card__option-pill--sold-out':
                    isOptionValueSoldOut(option.name, value),
                }"
                :disabled="isOptionValueSoldOut(option.name, value)"
                @click.stop="handlePillClick(option.name, optionIndex, value)"
              >
                {{ value }}
              </button>
            </div>
            <div v-else class="cart-freebie-card__option-wrapper">
              <select
                :id="'cart-freebie-multi-' + product.id + '-' + option.name"
                :value="selectedOptions[option.name]"
                class="cart-freebie-card__select"
                @change="
                  handleSelectChange(
                    option.name,
                    optionIndex,
                    $event.target.value,
                  )
                "
              >
                <option
                  v-for="value of getOptionValues(option.name, optionIndex)"
                  :key="value"
                  :value="value"
                  :disabled="isOptionValueSoldOut(option.name, value)"
                >
                  {{ value
                  }}{{
                    isOptionValueSoldOut(option.name, value)
                      ? ` (${$string('product.form.sold_out')})`
                      : ''
                  }}
                </option>
              </select>
              <icon-chevron-down
                class="cart-freebie-card__option-chevron"
                aria-hidden="true"
              />
            </div>
          </div>
          <btn
            v-if="!showCheckbox"
            class="cart-freebie-card__add-freebie"
            :disabled="!isSelectionValid"
            :label="$string('cart.freebies.add')"
            modifiers="secondary block center"
            size="small"
            type="button"
            @click.prevent="handleAddFreebie"
          />
        </div>
      </component>
    </template>

    <!-- Default mode: one card per variant (existing behavior) -->
    <template v-else>
      <component
        :is="showCheckbox ? 'label' : 'div'"
        v-if="variant?.availableState === 'available' || showCheckbox"
        class="cart-freebie-card__card"
        :for="showCheckbox ? name : undefined"
      >
        <template v-if="showCheckbox">
          <input
            :id="name"
            :checked="selected"
            class="cart-freebie-card__input"
            :disabled="disabled"
            :name="name"
            type="checkbox"
            @input.prevent.stop="handleInputChange"
          />
          <span
            class="cart-freebie-card__checkmark cart-freebie-card__checkbox"
            aria-hidden="true"
          >
            <icon-check-outline class="cart-freebie-card__check" />
          </span>
        </template>
        <span
          v-if="product && variant?.availableState !== 'available'"
          class="cart-freebie-card__sold-out text-mobile-paragraph-xs-bold text-desktop-paragraph-xs-bold"
          v-html="availableString"
        />

        <div class="cart-freebie-card__image-container">
          <responsive-image
            class="cart-freebie-card__image"
            :image="featuredImage"
            ratio="1:1"
            sizes="44px"
            widths="44, 106, 159"
          />
        </div>

        <div class="cart-freebie-card__details">
          <p
            class="cart-freebie-card__title text-mobile-paragraph-xs-bold text-desktop-paragraph-xs-bold"
            v-text="product?.title"
          />

          <p
            v-if="!product.hasOnlyDefaultVariant"
            class="cart-freebie-card__subtitle text-mobile-paragraph-xs-regular text-desktop-paragraph-xs-regular"
            v-text="variant?.title"
          />
        </div>
        <quick-add
          v-if="!showCheckbox"
          class="cart-freebie-card__quick-add"
          :product="product"
          :variant="variant"
          :freebie-handle="freebieHandle"
          :freebie-group-uid="freebieGroupUid"
          :show-page-toast="false"
          show-cart-toast
          :use-product-state="false"
        />
      </component>
    </template>
  </div>
</template>

<script>
/**
 * Vue: Cart freebie card (cart-freebies)
 * -----------------------------------------------------------------------------
 * Single variant tile for cart freebies.
 *
 * @param {String} name - Form input name.
 * @param {Boolean|Object} product - Product to display.
 * @param {Number} queueLimit - Queue limit.
 * @param {Number} queueSize - Queue size.
 * @param {Boolean} [selected] - Pre-select checkbox.
 * @param {Boolean} [showCheckbox] - Display checkbox.
 *
 * @emits queueVariant - Add variant ID to queue.
 *
 */
import Btn from '~global/btn/btn';
import ResponsiveImage from '~global/images/responsive-image';
import QuickAdd from '~async/quick-add/quick-add';
import { getFreebieAvailability } from '~/helpers/freebies';

import IconCheckOutline from '~icons/general/check-outline.svg';
import IconChevronDown from '~icons/directional-navigation/chevron/down.svg';

export default {
  name: 'CartFreebieCard',

  components: {
    Btn,
    IconCheckOutline,
    IconChevronDown,
    ResponsiveImage,
    QuickAdd,
  },

  provide() {
    return {
      variantsShowQatcOverlay: false,
    };
  },

  props: {
    name: {
      type: String,
      default: '',
      required: true,
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    queueLimit: {
      type: Number,
      default: 1,
      required: true,
    },
    queueSize: {
      type: Number,
      default: 0,
      required: true,
    },
    selected: {
      type: Boolean,
      default: false,
    },
    showCheckbox: {
      type: Boolean,
      default: false,
    },
    freebieHandle: {
      type: [Boolean, String],
      default: false,
    },
    freebieGroupUid: {
      type: String,
      default: null,
    },
    isMultipleVariantCard: {
      type: Boolean,
      default: false,
    },
    freebieVariantIds: {
      type: Array,
      default: null,
    },
  },

  emits: {
    'select-product': (variantId, selected) =>
      (typeof variantId === 'number' || typeof variantId === 'string') &&
      typeof selected === 'boolean',
    'add-variant': (variantId) =>
      typeof variantId === 'number' || typeof variantId === 'string',
  },

  data() {
    return {
      selectedOptions: {},
    };
  },

  computed: {
    /**
     * True when multi-variant card mode is active for this product.
     * Falls back to default mode for single-variant or default-variant products.
     */
    isMultiVariantMode() {
      if (!this.isMultipleVariantCard || !this.product) return false;
      if (this.product.hasOnlyDefaultVariant) return false;
      if (!this.freebieVariantIds || this.freebieVariantIds.length <= 1)
        return false;
      return true;
    },

    /**
     * Variants filtered to only those configured as freebie choices.
     */
    allowedVariants() {
      if (!this.product?.variants) return [];
      if (!this.freebieVariantIds?.length) return this.product.variants;
      return this.product.variants.filter((variant) =>
        this.freebieVariantIds.includes(variant.id.toString()),
      );
    },

    /**
     * Product options filtered to values that exist in allowedVariants.
     */
    filteredOptions() {
      if (!this.product?.options) return [];
      return this.product.options
        .map((option, optionIndex) => ({
          ...option,
          values: option.values.filter(
            (value) =>
              this.allowedVariants.some((variant) =>
                variant.selectedOptions?.some(
                  (so) => so.name === option.name && so.value === value,
                ),
              ) &&
              (optionIndex > 0 ||
                !this.isOptionValueSoldOut(option.name, value)),
          ),
        }))
        .filter((option) => option.values.length > 0);
    },

    /**
     * Available variants constrained to configured freebie choices.
     */
    availableVariants() {
      return this.allowedVariants.filter((variant) =>
        getFreebieAvailability({
          selectedOrFirstAvailableVariant: variant,
          allowHiddenVariants: this.product.allowHiddenVariants,
        }),
      );
    },

    /**
     * Variant matching the current dropdown selections, or null.
     */
    selectedVariant() {
      if (
        !this.filteredOptions.every((opt) =>
          Boolean(this.selectedOptions[opt.name]),
        )
      ) {
        return null;
      }
      return (
        this.allowedVariants.find((variant) =>
          this.filteredOptions.every((option) => {
            const variantOption = variant.selectedOptions?.find(
              (so) => so.name === option.name,
            );
            return variantOption?.value === this.selectedOptions[option.name];
          }),
        ) || null
      );
    },

    /**
     * True when the selected variant combination is valid and available.
     */
    isSelectionValid() {
      if (!this.selectedVariant) return false;
      return getFreebieAvailability({
        selectedOrFirstAvailableVariant: this.selectedVariant,
        allowHiddenVariants: this.product.allowHiddenVariants,
      });
    },

    /**
     * Disabled state for the checkbox in multi-variant mode.
     */
    multiVariantCheckboxDisabled() {
      if (!this.isSelectionValid) return true;
      return (
        this.queueSize >= this.queueLimit &&
        !this.selected &&
        this.queueLimit !== 1
      );
    },

    /**
     * Image for the multi-variant card — follows the selected variant, falls back to product image.
     */
    multiVariantImage() {
      const selectedVariant = this.selectedVariant;
      if (selectedVariant) {
        return (
          selectedVariant.localizedImage ||
          selectedVariant.featuredImage ||
          this.product.featuredImage
        );
      }
      return this.product.featuredImage;
    },

    /**
     * Compute featured image.
     */
    featuredImage() {
      if (this.variant) {
        if (this.variant.localizedImage) {
          return this.variant.localizedImage;
        }

        return this.variant.featuredImage;
      }

      return this.product.featuredImage;
    },

    /**

     * Compute available string.
     * @returns {String}
     */
    availableString() {
      if (this.variant.available) {
        return '';
      }

      if (this.variant.availableState === 'back-soon') {
        return this.$string('product.badges.back_soon_html');
      }

      return this.$string('product.badges.sold_out_html');
    },

    /**
     * Compute if the card should be disabled.
     * only disables when there are several items selectable
     * @returns {Boolean}
     */
    disabled() {
      return (
        !this.product ||
        !this.variant.available ||
        (this.queueSize >= this.queueLimit &&
          !this.selected &&
          this.queueLimit !== 1)
      );
    },

    variant() {
      // custom availability state. Just for freebies availability is true although hidden if we have stock
      let variant =
        this.product && this.product.selectedOrFirstAvailableVariant;
      if (variant) {
        if (getFreebieAvailability(this.product)) {
          variant = {
            ...variant,
            availableState: 'available',
            available: true,
          };
        }
      }
      return variant;
    },
  },

  created() {
    if (
      this.isMultiVariantMode &&
      this.product.selectedOrFirstAvailableVariant?.selectedOptions
    ) {
      const initialOptions = {};
      this.product.selectedOrFirstAvailableVariant.selectedOptions.forEach(
        (selectedOption) => {
          const optionIndex = this.filteredOptions.findIndex(
            (filteredOption) => filteredOption.name === selectedOption.name,
          );
          const option = this.filteredOptions[optionIndex];
          if (option && (optionIndex === 0 || option.values.length >= 3)) {
            initialOptions[selectedOption.name] = selectedOption.value;
          }
        },
      );
      this.selectedOptions = initialOptions;
    }
  },

  methods: {
    /**
     * Handle input change.
     * when there is only one freebie allowed to ensure it only allows
     * to add one freebie at a time. This is why the emit for queue variant
     * on input change
     */
    handleInputChange(event) {
      if (
        !this.selected &&
        this.queueLimit > 1 &&
        this.queueLimit === this.queueSize
      ) {
        event.target.checked = false;
        return false;
      }
      this.$emit('select-product', this.variant.id, !this.selected);

      return true;
    },

    /**
     * Check whether all variants with a given option value are sold out.
     * @param {String} optionName
     * @param {String} value
     * @returns {Boolean}
     */
    isOptionValueSoldOut(optionName, value) {
      const currentOptionIndex =
        this.product?.options?.findIndex(
          (option) => option.name === optionName,
        ) ?? -1;

      const variants = this.allowedVariants.filter((variant) => {
        if (
          !variant.selectedOptions?.some(
            (so) => so.name === optionName && so.value === value,
          )
        ) {
          return false;
        }
        // Constrain only by previously-selected options so dependency stays one-way.
        return Object.entries(this.selectedOptions).every(
          ([name, selected]) =>
            name === optionName ||
            !selected ||
            (this.product?.options?.findIndex(
              (option) => option.name === name,
            ) ?? -1) >= currentOptionIndex ||
            variant.selectedOptions?.some(
              (so) => so.name === name && so.value === selected,
            ),
        );
      });
      return (
        variants.length > 0 &&
        variants.every(
          (variant) =>
            !variant.available &&
            !(
              this.product.allowHiddenVariants &&
              variant.availableState === 'hidden' &&
              variant.inventory > 0
            ),
        )
      );
    },

    /**
     * Return option values that match already-selected previous options.
     * This enables cascading selects (e.g. first option filters second option).
     */
    getOptionValues(
      optionName,
      optionIndex,
      selections = this.selectedOptions,
    ) {
      const option = this.filteredOptions[optionIndex];
      if (!option) return [];

      return option.values.filter((value) =>
        this.availableVariants.some((variant) => {
          const hasCurrentValue = variant.selectedOptions?.some(
            (so) => so.name === optionName && so.value === value,
          );

          if (!hasCurrentValue) return false;

          return this.filteredOptions
            .slice(0, optionIndex)
            .every((previousOption) => {
              const selectedValue = selections[previousOption.name];
              if (!selectedValue) return true;
              return variant.selectedOptions?.some(
                (so) =>
                  so.name === previousOption.name && so.value === selectedValue,
              );
            });
        }),
      );
    },

    /**
     * Keep downstream selections in sync when a previous option changes.
     */
    normalizeSelections(startingOptionIndex = 0) {
      const updatedSelections = {
        ...this.selectedOptions,
      };

      this.filteredOptions.slice(startingOptionIndex + 1).forEach((option) => {
        const optionIndex = this.filteredOptions.findIndex(
          (filteredOption) => filteredOption.name === option.name,
        );
        const values = this.getOptionValues(
          option.name,
          optionIndex,
          updatedSelections,
        );
        if (!values.includes(updatedSelections[option.name])) {
          if (values[0]) {
            updatedSelections[option.name] = values[0];
          } else {
            delete updatedSelections[option.name];
          }
        }
      });

      this.selectedOptions = updatedSelections;
    },

    handleSelectChange(optionName, optionIndex, value) {
      this.selectedOptions = {
        ...this.selectedOptions,
        [optionName]: value,
      };
      this.normalizeSelections(optionIndex);
    },

    /**
     * Handle checkbox change in multi-variant mode.
     * Requires a valid variant selection before toggling.
     */
    handlePillClick(optionName, optionIndex, value) {
      if (this.isOptionValueSoldOut(optionName, value)) return;
      this.selectedOptions = {
        ...this.selectedOptions,
        [optionName]: value,
      };
      this.normalizeSelections(optionIndex);
    },

    handleMultiVariantCheckboxChange(event) {
      if (!this.isSelectionValid) {
        event.target.checked = this.selected;
        return;
      }
      if (
        !this.selected &&
        this.queueLimit > 1 &&
        this.queueLimit === this.queueSize
      ) {
        event.target.checked = false;
        return;
      }
      this.$emit('select-product', this.selectedVariant.id, !this.selected);
    },

    /**
     * Emit add-variant with the selected variant ID (single-qty multi-variant flow).
     */
    handleAddFreebie() {
      if (!this.isSelectionValid) return;
      this.$emit('add-variant', this.selectedVariant.id);
    },
  },
};
</script>

<style lang="scss">
@import './cart-freebie-card';
</style>
